import React, { useEffect, useState } from 'react';

interface AlertDialogProps {
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    type?: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
    isOpen,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    type = 'danger',
    onConfirm,
    onCancel,
    loading = false
}) => {
    const [shouldRender, setShouldRender] = useState(isOpen);

    useEffect(() => {
        if (isOpen) setShouldRender(true);
    }, [isOpen]);

    if (!shouldRender && !isOpen) return null;

    const typeConfig = {
        danger: {
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            ),
            iconBg: 'bg-danger-500/10',
            iconColor: 'text-danger-500',
            btnBg: 'bg-danger-600 hover:bg-danger-500 shadow-danger-500/20'
        },
        warning: {
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            ),
            iconBg: 'bg-orange-500/10',
            iconColor: 'text-orange-500',
            btnBg: 'bg-orange-600 hover:bg-orange-500 shadow-orange-500/20'
        },
        info: {
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            ),
            iconBg: 'bg-primary-500/10',
            iconColor: 'text-primary-500',
            btnBg: 'bg-primary-600 hover:bg-primary-500 shadow-primary-500/20'
        }
    };

    const config = typeConfig[type];

    return (
        <div
            className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onTransitionEnd={() => !isOpen && setShouldRender(false)}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-dark-950/80 backdrop-blur-sm" onClick={onCancel} />

            {/* Dialog */}
            <div className={`relative w-full max-w-md bg-dark-900 rounded-3xl border border-white/5 shadow-2xl p-6 sm:p-8 overflow-hidden transition-all duration-300 transform ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                <div className="flex flex-col items-center text-center">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-2xl ${config.iconBg} flex items-center justify-center mb-6`}>
                        <svg className={`w-7 h-7 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {config.icon}
                        </svg>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
                    <p className="text-dark-400 text-sm leading-relaxed mb-8">
                        {message}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 w-full">
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="flex-1 px-6 py-3 rounded-xl bg-dark-800 text-white font-semibold text-sm hover:bg-dark-700 transition-colors disabled:opacity-50"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className={`flex-1 px-6 py-3 rounded-xl ${config.btnBg} text-white font-semibold text-sm shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 group disabled:opacity-50`}
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : null}
                            <span>{confirmLabel}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlertDialog;
